import React from "react";
import { Plus, Tag, Eye, EyeOff, Trash2, Calendar, Megaphone, LayoutGrid, Pencil, X, Save } from "lucide-react";
import { Input } from "../components/ui/input";
import { SectionCard, EmptyState } from "./AdminUI";
import { SiteBannerNotice } from "./SiteBannerNotice";

const PromotionsTab = ({ ctx }) => {
  const {
    promotions, promoForm, setPromoForm,
    handleCreatePromo, handleTogglePromo, handleDeletePromo,
    handleEditPromo, handleCancelEditPromo, editingPromoId,
  } = ctx;

  const isEditing = !!editingPromoId;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Tag size={20} className="text-[#FF3B30]" />
          Gestion des promotions
        </h2>
        <span className="text-white/50 text-xs">{promotions.filter((p) => p.is_active).length} active(s)</span>
      </div>

      <SiteBannerNotice onOpen={ctx.openSiteBanner} />

      {/* Create / Edit Form */}
      <SectionCard
        title={isEditing ? "✏️ Modifier la promotion" : "Nouvelle promotion"}
        icon={isEditing ? Pencil : Plus}
      >
        <form onSubmit={handleCreatePromo} className="space-y-4">
          {isEditing && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-xs text-amber-200 leading-relaxed">
              <Pencil size={12} className="inline -mt-0.5 mr-1" />
              Tu modifies une promotion existante. Enregistre pour appliquer, ou{" "}
              <button type="button" onClick={handleCancelEditPromo} className="underline hover:text-white" data-testid="promo-cancel-edit-inline">annule</button>{" "}
              pour recréer une nouvelle.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Titre *</label>
              <Input
                value={promoForm.title}
                onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
                placeholder="Ex: Offre de rentrée"
                data-testid="promo-title-input"
                style={{ minHeight: 44 }}
                className="bg-[#1C1C1E] border-white/[0.06] text-white text-base md:text-sm"
              />
            </div>
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Réduction</label>
              <Input
                value={promoForm.discount}
                onChange={(e) => setPromoForm({ ...promoForm, discount: e.target.value })}
                placeholder="Ex: -20% ou 50€"
                data-testid="promo-discount-input"
                style={{ minHeight: 44 }}
                className="bg-[#1C1C1E] border-white/[0.06] text-white text-base md:text-sm"
              />
            </div>
          </div>
          <div>
            <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Description *</label>
            <textarea
              value={promoForm.description}
              onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
              placeholder="Décrivez l'offre en détail..."
              data-testid="promo-description-input"
              rows={3}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] text-white text-base md:text-sm p-3 rounded-md resize-none focus:outline-none focus:border-white/20"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block flex items-center gap-1">
                <Calendar size={11} /> Date de fin
              </label>
              <Input
                type="date"
                value={promoForm.valid_until}
                onChange={(e) => setPromoForm({ ...promoForm, valid_until: e.target.value })}
                data-testid="promo-date-input"
                style={{ minHeight: 44 }}
                className="bg-[#1C1C1E] border-white/[0.06] text-white text-base md:text-sm"
              />
            </div>
            <div className="flex items-center gap-4 h-11 md:h-9">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-white/70">
                <input
                  type="checkbox"
                  checked={promoForm.show_banner}
                  onChange={(e) => setPromoForm({ ...promoForm, show_banner: e.target.checked })}
                  className="w-3.5 h-3.5 accent-[#FF3B30]"
                />
                <Megaphone size={11} /> Bandeau
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-white/70">
                <input
                  type="checkbox"
                  checked={promoForm.show_section}
                  onChange={(e) => setPromoForm({ ...promoForm, show_section: e.target.checked })}
                  className="w-3.5 h-3.5 accent-[#FF3B30]"
                />
                <LayoutGrid size={11} /> Section
              </label>
            </div>
            <div className="flex items-center gap-2">
              {isEditing && (
                <button
                  type="button"
                  onClick={handleCancelEditPromo}
                  data-testid="promo-cancel-btn"
                  className="bg-white/[0.06] hover:bg-white/[0.1] text-white/70 px-4 py-2 rounded-md text-sm font-semibold transition-colors h-11 md:h-9 flex items-center gap-2"
                >
                  <X size={14} /> Annuler
                </button>
              )}
              <button
                type="submit"
                data-testid="promo-submit-btn"
                className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors h-11 md:h-9 flex items-center gap-2"
              >
                {isEditing ? <><Save size={14} /> Enregistrer</> : <><Plus size={14} /> Créer</>}
              </button>
            </div>
          </div>
        </form>
      </SectionCard>

      {/* List */}
      {promotions.length === 0 ? (
        <EmptyState icon={Tag} title="Aucune promotion créée" description="Créez votre première offre ci-dessus." />
      ) : (
        <div className="space-y-2.5">
          {promotions.map((promo) => (
            <div
              key={promo.id}
              data-testid={`promo-card-${promo.id}`}
              className={`bg-[#141414] border rounded-xl p-4 hover:border-white/[0.12] transition-colors ${
                editingPromoId === promo.id
                  ? "border-[#FF3B30]/50 ring-1 ring-[#FF3B30]/20"
                  : promo.is_active ? "border-emerald-500/20" : "border-white/[0.06] opacity-60"
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <h4 className="font-semibold text-white">{promo.title}</h4>
                    {promo.discount && (
                      <span className="bg-[#FF3B30] text-white px-2 py-0.5 rounded-md text-[11px] font-bold">
                        {promo.discount}
                      </span>
                    )}
                    {promo.is_active ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400 text-[11px] font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Actif
                      </span>
                    ) : (
                      <span className="text-white/40 text-[11px]">○ Inactif</span>
                    )}
                    {editingPromoId === promo.id && (
                      <span className="inline-flex items-center gap-1 text-[#FF3B30] text-[11px] font-medium">
                        <Pencil size={10} /> en cours d'édition
                      </span>
                    )}
                  </div>
                  <p className="text-white/70 text-sm">{promo.description}</p>
                  <div className="flex items-center flex-wrap gap-3 text-[11px] text-white/40 mt-2">
                    {promo.valid_until && (
                      <span className="flex items-center gap-1">
                        <Calendar size={11} />
                        Jusqu'au {new Date(promo.valid_until).toLocaleDateString("fr-FR")}
                      </span>
                    )}
                    {promo.show_banner && (
                      <span className="text-cyan-400 flex items-center gap-1"><Megaphone size={11} /> Bandeau</span>
                    )}
                    {promo.show_section && (
                      <span className="text-purple-400 flex items-center gap-1"><LayoutGrid size={11} /> Section</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    onClick={() => handleEditPromo(promo)}
                    data-testid={`promo-edit-${promo.id}`}
                    className="p-2 bg-white/[0.04] text-white/70 hover:bg-white/[0.08] hover:text-white border border-white/[0.08] rounded-md transition-colors"
                    title="Modifier"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleTogglePromo(promo.id)}
                    data-testid={`promo-toggle-${promo.id}`}
                    className={`p-2 rounded-md border transition-colors ${
                      promo.is_active
                        ? "bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border-amber-500/20"
                        : "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border-emerald-500/20"
                    }`}
                    title={promo.is_active ? "Désactiver" : "Activer"}
                  >
                    {promo.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    onClick={() => handleDeletePromo(promo.id)}
                    data-testid={`promo-delete-${promo.id}`}
                    className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 rounded-md transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PromotionsTab;
